import React, { useEffect, useState } from "react";
import Select from "../../../form/select/Select";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import { postAddEdit, postDetails } from "../../../../services/postService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Input from "../../../form/Input";
import { toast } from "react-toastify";
import { taxonomyList } from "../../../../services/taxonomyService";
import Editor from "../../../elements/tiptap-editor/Editor";
import DatePicker from "../../../form/DatePicker";
import Textarea from "../../../form/Textarea";
import MultiselectDropdown from "../../../form/MultiselectDropdown";
import Tags from "../../../form/Tags";
import { useNavigate, useParams } from "react-router-dom";
import SelectedMediaCard from "../../../elements/SelectedMediaCard";
import SelectImageGallery from "../../../form/SelectImageGallery";
import SelectSingleImage from "../../../form/SelectSingleImage";
import FlexibleSelect from "../../../form/select/FlexibleSelect";
import LanguageTabs from "../../../elements/LanguageSwitcher";
import { generateSlug, supportedLanguages, userLang } from "../../../../helpers";
import { userList } from "../../../../services/userService";

const validation = {
  title: { required: true, message: "Title is required" },
};

const ManageMenu = ({ getResponse = () => {}, close = () => {} }) => {
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const id = params?.id;
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [coverMedia, setCoverMedia] = useState("");
  const { values, handleChange, handleSubmit, errors, setFieldsValue, resetField, formValidate } = useForm({
    validationRules: validation,
    initialState: { status: "active" },
  });
  const [categories, setCategories] = useState([]);
  const [currentLang, setCurrentLang] = useState(localStorage.getItem("lang") || "en");
  const [variants, setVariants] = useState([]);
  const [showVariants, setShowVariants] = useState(false);

  const handleLangChange = (lang) => {
    setCurrentLang(lang);
  };

  const handleVariantChange = (index, field, value) => {
  setVariants((prevVariants) => {
    const updated = [...prevVariants];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };

    if (field === "name") {
      updated[index].slug = generateSlug(value,Date.now());
    }

    return updated;
  });
};


  const handleAddVariant = () => {
    setVariants([...variants, { name: "", price: "" }]);
  };

  const handleRemoveVariant = (index) => {
    const updatedVariants = variants.filter((_, i) => i !== index);
    setVariants(updatedVariants);
  };

  const { data: details } = useQuery({
    queryKey: ["menu-details", id],
    queryFn: async () => {
      const res = await postDetails({ id: id });
      return res?.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (details?._id) {
      setFieldsValue({
        ...details,
        content: {
          ...supportedLanguages.reduce((acc, lang) => {
            acc[lang.code] = {
              title: details?.content?.[lang.code]?.title || "",
              content: details?.content?.[lang.code]?.content || "",
              excerpt: details?.content?.[lang.code]?.excerpt || "",
              seo: details?.content?.[lang.code]?.seo || {},
            };
            return acc;
          }, {}),
        },
        title: details?.content?.[currentLang]?.title || "",
      });
      if (details?.variants?.length) {
        setVariants(
          details.variants.map((v) => ({
            name: v.name || "",
            slug: v.slug || "",
            price: v.price || "",
            currency: v.currency,
          }))
        );
      }

      setCategories(details?.categories);
      setCoverMedia(details?.coverMedia);
      setSelectedMedia(details?.mediaGallery);
    }
  }, [details]);

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      const media =
        selectedMedia?.length > 0
          ? selectedMedia.reduce((acc, item, index) => {
              if (item?._id) acc[`mediaGallery[${index}]`] = item?._id;
              return acc;
            }, {})
          : { mediaGallery: "null" };

      return await postAddEdit({
        content: val?.content,
        slug: `${generateSlug(val?.content?.[userLang]?.title.replace(/\s+/g, "-").toLowerCase(), Date.now())}`,
        lang: localStorage.getItem("lang"),
        status: val?.status,
        points: val?.points,
        type: "product",
        coverMedia: coverMedia?._id ? coverMedia?._id : "null",
        variants: variants.length > 0 ? variants.filter((v) => v.name?.trim() || v.price?.toString().trim()) : "null",
        categories: categories,
        ...(details?._id && { id: details._id }),
        ...media,
      });
    },

    onSuccess: (res) => {
      getResponse(res);
      queryClient.invalidateQueries({ queryKey: ["menu-details", id] });
      if (res?.status === 200 || res?.status === 201) {
        navigate(`/admin/menu`);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });
  const { data: categoryData } = useQuery({
    queryKey: ["category"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "product-category",
        status: "active",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  return (
    <>
      <form onSubmit={(event) => handleSubmit(event, mutate)}>
        <div className="flex justify-between items-center w-full md:px-6 flex-col xls:flex-row space-y-3">
          <div className="font-semibold text-lg xls:text-sm xl:text-3xl text-primary text-right xls:text-left w-full">Manage Menu Item</div>
          <LanguageTabs languages={supportedLanguages} defaultLang="en" value={currentLang} onChange={handleLangChange} />
          <div className="flex gap-3 w-full justify-center md:justify-end">
            <Button
              buttonType="submit"
              buttonLabel="Save Menu Item"
              buttonClasses="min-w-28"
              buttonLabelClasses="mx-auto"
              {...(isLoading && {
                loading: true,
                buttonIconPosition: "left",
                isDisable: true,
              })}
            />
            <Button
              buttonLabel="Cancel"
              buttonClasses="min-w-28 !bg-white !text-primary border !border-primary"
              buttonLabelClasses="mx-auto"
              buttonHasLink={true}
              buttonLink={"/admin/menu"}
            />
          </div>
        </div>

        <div className="flex flex-col-reverse lg:grid grid-cols-4 gap-6 px-0 py-6 md:p-6">
          <div className="col-span-12 lg:col-span-3 space-y-4">
            <Input
              label="Title"
              inputPlaceholder="Enter title"
              onChange={(e) => {
                const newValue = e.target.value;
                setFieldsValue({
                  ...values,
                  title: newValue,
                  content: {
                    ...values.content,
                    [currentLang]: {
                      ...values.content?.[currentLang],
                      title: newValue,
                    },
                  },
                });
              }}
              inputName={"title"}
              required={false}
              value={values?.content?.[currentLang]?.title || ""}
              {...formValidate(errors, "title")}
            />

            <Textarea
              label="Content"
              inputPlaceholder="Content..."
              inputClasses="!h-[180px]"
              inputName="content"
              inputValue={values?.content?.[currentLang]?.content || ""}
              onChange={(e) => {
                const newValue = e.target.value;
                setFieldsValue({
                  ...values,
                  content: {
                    ...values.content,
                    [currentLang]: {
                      ...values.content?.[currentLang],
                      content: newValue,
                    },
                  },
                });
              }}
            />
            <div className="bg-white p-4 rounded-md border border-text-secondary/50">
              <SelectImageGallery label="Choose Media" values={selectedMedia} onChange={(newMedia) => setSelectedMedia(newMedia)} isSingle={false} />
            </div>

            <div className="border border-gray-300 rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold text-primary">Variants</h3>

                {!showVariants && (
                  <button
                    type="button"
                    onClick={() => setShowVariants(true)}
                    className="flex items-center gap-2 text-primary hover:text-primary/80 transition-all"
                  >
                    <i class="fa-solid fa-chevron-down"></i>
                  </button>
                )}
              </div>

              {showVariants && (
                <div className="space-y-4">
                  {variants.length > 0 ? (
                    variants.map((variant, index) => (
                      <div key={index} className="flex flex-wrap items-end gap-3 border border-gray-200 p-4 rounded-lg bg-gray-50 relative">
                        <div className="flex-1 min-w-[200px]">
                          <Input
                            label="Variant Name"
                            inputPlaceholder="Enter name"
                            value={variant.name}
                            onChange={(e) => handleVariantChange(index, "name", e.target.value)}
                          />
                        </div>

                        <div className="w-[180px]">
                          <Input
                            label="Price ($)"
                            inputType="number"
                            inputPlaceholder="0"
                            value={variant.price}
                            onChange={(e) => handleVariantChange(index, "price", e.target.value)}
                          />
                        </div>

                        <button
                          type="button"
                          onClick={() => handleRemoveVariant(index)}
                          className="absolute top-3 right-3 text-red-500 hover:text-red-600 transition-colors"
                        >
                          ✕
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="py-4 text-sm text-gray-600">No variants yet. Add one to get started.</div>
                  )}

                  <div className="flex justify-between items-center mt-3">
                    <Button
                      buttonLabel="Add Variant"
                      buttonClasses="!bg-primary text-white !py-1 !px-3 !text-sm rounded-md shadow-sm"
                      buttonFunction={() => setVariants((prev) => [...prev, { name: "", slug: "", price: "" }])}
                      type="button"
                    />

                    <button type="button" onClick={() => setShowVariants(false)} className="text-sm text-red-500 hover:text-red-600 font-medium">
                      <i class="fa-solid fa-chevron-up"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="relative col-span-4 mt-5">
              <div className="flex gap-4">
                <Button
                  buttonType="submit"
                  buttonLabel={"Save Menu Item"}
                  buttonClasses={"min-w-28"}
                  buttonLabelClasses={"mx-auto"}
                  {...(isLoading && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
                <Button
                  buttonLabel={"Cancel"}
                  buttonClasses={"min-w-28 !bg-white !text-primary border !border-primary"}
                  buttonLabelClasses={"mx-auto"}
                  buttonHasLink={true}
                  buttonLink={"/admin/menu"}
                />
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-1">
            <div className="flex flex-col md:flex-row lg:flex-col space-y-6 md:space-y-0 lg:space-y-6 gap-x-3 items-start justify-between w-full">
              <div className="border border-gray-300 rounded-lg p-4 space-y-2 flex-shrink-0 w-full md:w-[22%] lg:w-full">
                <SelectSingleImage
                  label="Upload Cover Media"
                  size="lg"
                  value={coverMedia}
                  onChange={(img) => setCoverMedia(img)}
                  onRemove={() => setCoverMedia(null)}
                />
              </div>

              <div className="border border-gray-300 rounded-lg p-4 space-y-2 flex-shrink-0 w-full md:w-[75%] lg:w-full">
                {/* <DatePicker
                label={"Publish Start Date"}
                placeholder={"Start date"}
                position="fixed"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "publishStartDate",
                      value: e?.startDate || e?.endDate,
                    },
                  })
                }
                value={{
                  startDate: values?.publishStartDate,
                  endDate: values?.publishStartDate,
                }}
                // {...formValidate(errors, "date")}
              />
              <DatePicker
                label={"Publish End Date"}
                placeholder={"End date"}
                position="fixed"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "publishEndDate",
                      value: e?.startDate || e?.endDate,
                    },
                  })
                }
                value={{
                  startDate: values?.publishEndDate,
                  endDate: values?.publishEndDate,
                }}
                // {...formValidate(errors, "date")}
              /> */}
                <FlexibleSelect
                  label={"Status"}
                  placeholder={"Choose status"}
                  onChange={(e) => handleChange({ target: { name: "status", value: e } })}
                  selectedValue={values?.status}
                  dropdownData={[
                    { name: "Active", value: "active" },
                    { name: "Inactive", value: "inactive" },
                  ]}
                  {...formValidate(errors, "status")}
                />
                {/* <LanguageTabs languages={supportedLanguages} defaultLang="en" value={currentLang} onChange={handleLangChange} /> */}
                <FlexibleSelect
                  label={"Select Menu Category"}
                  isSearch={true}
                  dropdownData={categoryData?.data?.map((item) => {
                    return {
                      name: item?.content?.[userLang]?.title,
                      value: item?._id,
                    };
                  })}
                  selectedValue={categories}
                  onChange={setCategories}
                  type="multiple"
                  placeholder="Choose categories"
                />
                {/* <FlexibleSelect
                label={"Select Artist"}
                isSearch={true}
                dropdownData={userData?.data?.map((item) => {
                  return {
                    name: `${item?.firstName} ${item?.lastName}`,
                    value: item?._id,
                    image: item?.image?.url,
                  };
                })}
                selectedValue={artist}
                onChange={setArtist}
                type="single"
                placeholder={"Pick Artist"}
              /> */}
                {/* <Input
                label={"Reward Points"}
                inputPlaceholder="Add Points"
                inputName={"points"}
                value={values?.points}
                onChange={handleChange}
                //   {...formValidate(errors, "lastName")}
              /> */}
                {/* <Tags
                label="Tags"
                placeholder="Enter tags"
                options={tagsData?.data?.map((item) => ({
                  label: item.content?.[userLang]?.title,
                  _id: item._id,
                }))}
                selectedItems={tags}
                setSelectedItems={setTags}
                tagsType="input"
              /> */}
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default ManageMenu;
